import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import api from "@/lib/api";

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications);
      setUnread(data.unread);
    } catch (e) {}
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try { await api.post("/notifications/read"); setUnread(0); } catch (e) {}
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} className="p-2 rounded-full hover:bg-secondary transition-colors relative" data-testid="notification-bell" aria-label="Notifications">
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-unread">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-card rounded-2xl border border-border shadow-xl overflow-hidden z-50" data-testid="notification-panel">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <span className="font-display font-semibold">Notifications</span>
            {items.length > 0 && <CheckCheck className="h-4 w-4 text-muted-foreground" />}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">No notifications yet.</p>
            ) : items.map((n) => (
              <button
                key={n.notification_id}
                onClick={() => { setOpen(false); if (n.link) navigate(n.link); }}
                className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-secondary/50 transition-colors ${!n.read ? "bg-accent/5" : ""}`}
                data-testid={`notification-item-${n.notification_id}`}
              >
                <p className="text-sm font-medium">{n.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
